import type { AxiosInstance } from "axios";

import { toast } from "sonner";

import type { Order, OrderStatusEnum } from "@/types/order";

import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { usePrivateAxios } from "./use-private-axios";

interface BaseOrderResponse {
	success: boolean;
	message: string;
}
interface OrdersResponse extends BaseOrderResponse {
	data: {
		totalItems: number;
		currentPage: number;
		itemsPerPage: number;
		totalPages: number;
		data: Order[];
	};
}
interface OrderResponse extends BaseOrderResponse {
	data: Order;
}

interface OrderQueryParams {
	startDate?: string;
	endDate?: string;
	status?: string;
	filter?: string;
	page?: number;
	limit?: number;
}

async function fetchOrders(axios: AxiosInstance, params?: OrderQueryParams) {
	const { data } = await axios.get<OrdersResponse>(`/order/list_orders`, { params });
	return data.data;
}
async function fetchOrder(axios: AxiosInstance, orderId: string) {
	const { data } = await axios.get<OrderResponse>(`/order/${orderId}`);
	return data.data;
}
export function useOrders(params?: OrderQueryParams) {
	const privateAxios = usePrivateAxios();
	return useQuery({
		queryKey: ["orders"],
		placeholderData: keepPreviousData,
		queryFn: () => fetchOrders(privateAxios, params)
	});
}

export function useOrder(orderId: string, initialData?: Order) {
	const privateAxios = usePrivateAxios();
	return useQuery({
		queryKey: ["order", orderId],
		queryFn: () => fetchOrder(privateAxios, orderId),
		initialData,
		enabled: !!orderId
	});
}
export function useUpdateOrderStatus() {
	const privateAxios = usePrivateAxios();
	const queryClient = useQueryClient();
	return useMutation({
		mutationKey: ["order", "status"],
		mutationFn: async (body: { orderId: string; status: OrderStatusEnum }) => {
			const { data } = await privateAxios.put("/order/update-status", body);
			return data.data;
		},
		onSuccess: () => toast.success("Order status updated successfully"),
		onError: () => toast.error("Failed to update order status"),
		onSettled: (_, __, v) => queryClient.invalidateQueries({ queryKey: ["order", v.orderId] })
	});
}

export function useUpdateOrderDropoff() {
	const privateAxios = usePrivateAxios();
	return useMutation({
		mutationKey: ["order", "dropoff"],
		mutationFn: async (body: { courierOfficeId: number; estimatedWeight?: number; actualWeight?: number; contractId: string }) => {
			const { courierOfficeId, estimatedWeight, actualWeight } = body;
			const { data } = await privateAxios.put(`/order/contract/${body.contractId}/update-dropoff-location`, { courierOfficeId, estimatedWeight, actualWeight });
			return data.data;
		},
		onSuccess: () => toast.success("Dropoff location updated successfully"),
		onError: () => toast.error("Failed to update dropoff location")
	});
}
